import React, { useContext, useMemo } from 'react';
import {
    EntityValidations,
    ISpecificEntityFormProps,
    IApp04Topic,
    App04Topic,
    App04TopicUser,
} from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from '../cr/CrDropdown';
import { CrTextField } from '../cr/CrTextField';
import { CrUserPicker } from '../cr/CrUserPicker';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { AppIDs } from '../../AppGlobals';
import { OrbUserContext } from '../OrbUserContext';

class App04TopicFormValidations extends EntityValidations {
    public Owners: string = null;
    public AppID: string = null;
    public WebSearch: string = null;
    public WebSearchSites: string = null;
}

interface App04TopicFormProps extends ISpecificEntityFormProps {
    menuData: any[];
}

export const App04TopicForm = (props: App04TopicFormProps): React.ReactElement => {

    const { disableGroupManagement } = useContext(IntegrationContext);
    const { userContext } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;

    const {
        dataServices,
        lookupData,
        loadLookupData: {
            entityStatuses,
            users: { all: allUsers },
        },
    } = useContext(DataContext);

    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (topic: IApp04Topic): Promise<App04TopicFormValidations> => {

        const errors = new App04TopicFormValidations();

        if (!topic.Title) {
            errors.Title = 'Topic title is required';
            errors.Valid = false;
        }

        if (!topic.WebSearch) {
            errors.WebSearch = 'Please select a web search option';
            errors.Valid = false;
        }

        if (topic.WebSearch === 'Sites' && !topic.WebSearchSites?.trim()) {
            errors.WebSearchSites = 'Please provide at least one website';
            errors.Valid = false;
        }

        if (!topic.App04TopicUsers || topic.App04TopicUsers.length === 0) {
            errors.Owners = 'At least one owner is required';
            errors.Valid = false;
        }

        if (topic.AppID == null) {
            errors.AppID = "Please select an app";
            errors.Valid = false;
        }
        else {
            errors.AppID = null;
        }

        return Promise.resolve(errors);
    };

    const NO_APP_ACCESS = -1;

    const appOptions = useMemo(() => [
        { ID: NO_APP_ACCESS, Title: "No Data Access" },
        ...props.menuData
            .filter(app => app.ID !== AppIDs.CLOEAI)
            .map(app => ({
                ID: app.ID,
                Title: `${app.AppName} Data`
            }))
    ], [props.menuData]);

    const saveTopic = (topic: IApp04Topic) => {
        const entity = {
            ...topic,
            AppID: topic.AppID === NO_APP_ACCESS ? null : topic.AppID
        };

        return entity;
    };

    return (
        <EntityForm<IApp04Topic, App04TopicFormValidations>
            {...props}
            entityName="Topic"

            renderFormFields={(changeHandlers, formState) => {

                const {
                    FormData: topic,
                    ValidationErrors: errors,
                } = formState;

                return (
                    <div>

                        <CrTextField
                            label="Title"
                            className={styles.formField}
                            required={true}
                            maxLength={500}
                            value={topic.Title}
                            errorMessage={errors.Title}
                            onChange={(ev, v) => changeHandlers.changeTextField(v, 'Title')}
                        />

                        <CrTextField
                            label="Details"
                            className={styles.formField}
                            multiline
                            rows={6}
                            maxLength={4000}
                            value={topic.Description}
                            onChange={(ev, v) => changeHandlers.changeTextField(v, 'Description')}
                        />

                        <CrDropdown
                            label="Access App Data"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.AppID}
                            options={LookupService.entitiesToSelectableOptions(appOptions)}
                            selectedKey={topic.AppID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, "AppID")}
                        />

                        <CrDropdown
                            label="Web Search"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.WebSearch}
                            options={LookupService.entitiesToSelectableOptionsCustom([
                                { ID: 'Required', Title: 'Search web if required' },
                                { ID: 'Never', Title: 'Never search the web' },
                                { ID: 'Always', Title: 'Always search the web' },
                                { ID: 'Sites', Title: 'Only search specified sites' },
                            ])}
                            selectedKey={topic.WebSearch}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'WebSearch')}
                        />

                        {topic.WebSearch === 'Sites' && (
                            <CrTextField
                                label="Web Search List"
                                className={styles.formField}
                                multiline
                                rows={6}
                                maxLength={4000}
                                required={true}
                                errorMessage={errors.WebSearchSites}
                                value={topic.WebSearchSites}
                                placeholder={`One website per line\nhttps://example.com\nhttps://contoso.com`}
                                onChange={(ev, v) => changeHandlers.changeTextField(v, 'WebSearchSites')}
                            />
                        )}

                        <CrUserPicker
                            label="Owner(s)"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            itemLimit={5}
                            required={true}
                            errorMessage={errors.Owners}
                            selectedUsers={topic.App04TopicUsers?.map(x => x.UserID)}
                            onChange={v =>
                                changeHandlers.changeMultiUserPicker(
                                    v,
                                    'App04TopicUsers',
                                    new App04TopicUser(),
                                    'UserID'
                                )
                            }
                        />

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(
                                lookupData.EntityStatuses
                            )}
                            selectedKey={topic.EntityStatusID}
                            onChange={(_, o) =>
                                changeHandlers.changeStatusDropdown(
                                    o,
                                    'EntityStatusID'
                                )
                            }
                        />

                    </div>
                );
            }}

            loadEntity={id =>
                dataServices.app04TopicService
                    .read(id, true, true)
                    .then(topic => ({
                        ...topic,
                        AppID: topic.AppID == null ? NO_APP_ACCESS : topic.AppID
                    }))
            }
            loadNewEntity={() => {
                const topic = new App04Topic();
                topic.App04TopicUsers = [
                    {
                        ...new App04TopicUser(),
                        UserID: currentUserId,
                    },
                ];

                return topic;
            }}
            loadEntityValidations={() => new App04TopicFormValidations()}
            onValidateEntity={validateEntity}
            onCreate={t => dataServices.app04TopicService.create(saveTopic(t))}
            onUpdate={t => dataServices.app04TopicService.update(t.ID, saveTopic(t))}

            parentEntities={dataServices.app04TopicService.parentEntities}

            childEntities={[
                {
                    ObjectParentProperty: 'App04TopicUsers',
                    ParentIdProperty: 'TopicID',
                    ChildIdProperty: 'UserID',
                    ChildService: dataServices.app04TopicUserService,
                },
            ]}
        />
    );
};